import React, { useState, useRef } from 'react';
import { Modal } from '../../common/Modal';
import { messageFromError } from '../parts/errorMessage';
import { missingMandatory, yearError } from '../parts/inquiryFields';

// Capture a customer request before any job exists. Five mandatory fields,
// two optional. Kept deliberately short: this is typed at the bay or during a
// phone call, and a form long enough to abandon mid-entry is a form that goes
// back to being a paper notepad.

const EMPTY = {
  customer_name: '',
  customer_phone: '',
  year: '',
  make: '',
  model: '',
  customer_email: '',
  inquiry_notes: '',
};

const field =
  'w-full rounded-card border border-border-default bg-surface-card px-3 py-3 ' +
  'text-ink placeholder:text-ink-muted';

export const QuickIntakeModal = ({ isOpen, onClose, onSubmit, submitting = false }) => {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);
  // Bumped by handleClose so an in-flight submit that the mechanic has
  // already cancelled can recognise itself as abandoned when it settles, and
  // skip writing over whatever (or whoever) is on screen by then.
  const attemptRef = useRef(0);

  const set = (name) => (event) => {
    setForm((prev) => ({ ...prev, [name]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const missing = missingMandatory(form);
    if (missing.length > 0) {
      setError(`Still needed: ${missing.join(', ')}`);
      return;
    }
    // Mirrors the server's `year: ge=1900, le=2030`. Without this, `16` for a
    // 2016 Civic is a 422 round trip -- and a 422's `detail` is a LIST, which
    // is exactly the shape that used to white-screen the app.
    const badYear = yearError(form.year);
    if (badYear) {
      setError(badYear);
      return;
    }
    setError(null);

    const payload = {
      customer_name: form.customer_name.trim(),
      customer_phone: form.customer_phone.trim(),
      year: Number(form.year),
      make: form.make.trim(),
      model: form.model.trim(),
    };
    // Optional fields are omitted rather than sent as empty strings, so the
    // column stays NULL instead of holding ''.
    if (form.customer_email.trim()) payload.customer_email = form.customer_email.trim();
    if (form.inquiry_notes.trim()) payload.inquiry_notes = form.inquiry_notes.trim();

    // Captured before the await: if handleClose bumps attemptRef while this
    // request is in flight, that means the mechanic cancelled and possibly
    // started on a different customer. Whatever this request resolves to
    // belongs to a submit nobody is waiting on any more.
    const attempt = attemptRef.current;

    try {
      await onSubmit(payload);
      if (attempt !== attemptRef.current) return; // abandoned -- already closed/reset by handleClose
      setForm(EMPTY);
      onClose();
    } catch (err) {
      if (attempt !== attemptRef.current) return; // abandoned -- do not paint a stale error over the next customer
      // Prefer the server's message (a 400 names the missing/invalid field, a
      // 409 names the conflicting job) and fall back to a plain one. Either
      // way: stay open, keep what was typed -- the mechanic must not be left
      // guessing whether the save went through.
      //
      // messageFromError, not a raw `detail` read: a 422's detail is an array
      // of objects, which is truthy (so the `||` fallback never fired) and
      // unrenderable (so React unmounted the whole app).
      setError(
        messageFromError(err, 'Could not save this inquiry. Please try again.')
      );
    }
  };

  // Reset on any close that is NOT a successful save (Cancel, backdrop click,
  // Escape, the X button) so the next customer never sees a half-typed
  // leftover from the last one. Bumping attemptRef here is what lets a
  // still-in-flight submit recognise, once it settles, that it was cancelled.
  const handleClose = () => {
    attemptRef.current += 1;
    setForm(EMPTY);
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="New inquiry" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div
            role="alert"
            className="rounded-card border border-border-default bg-surface-card px-3 py-2 text-small text-ink"
          >
            {error}
          </div>
        )}

        <div>
          <label htmlFor="intake-name" className="block text-small text-ink-soft">
            Customer name
          </label>
          <input
            id="intake-name"
            className={field}
            value={form.customer_name}
            onChange={set('customer_name')}
          />
        </div>

        <div>
          <label htmlFor="intake-phone" className="block text-small text-ink-soft">
            Phone number
          </label>
          <input
            id="intake-phone"
            type="tel"
            className={field}
            value={form.customer_phone}
            onChange={set('customer_phone')}
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label htmlFor="intake-year" className="block text-small text-ink-soft">
              Year
            </label>
            <input
              id="intake-year"
              type="number"
              inputMode="numeric"
              className={field}
              value={form.year}
              onChange={set('year')}
            />
          </div>
          <div>
            <label htmlFor="intake-make" className="block text-small text-ink-soft">
              Make
            </label>
            <input
              id="intake-make"
              className={field}
              value={form.make}
              onChange={set('make')}
            />
          </div>
          <div>
            <label htmlFor="intake-model" className="block text-small text-ink-soft">
              Model
            </label>
            <input
              id="intake-model"
              className={field}
              value={form.model}
              onChange={set('model')}
            />
          </div>
        </div>

        <div>
          <label htmlFor="intake-email" className="block text-small text-ink-soft">
            Email <span className="text-ink-muted">(optional)</span>
          </label>
          <input
            id="intake-email"
            type="email"
            className={field}
            value={form.customer_email}
            onChange={set('customer_email')}
          />
        </div>

        <div>
          <label htmlFor="intake-notes" className="block text-small text-ink-soft">
            What did they say? <span className="text-ink-muted">(optional)</span>
          </label>
          <textarea
            id="intake-notes"
            rows={3}
            className={field}
            value={form.inquiry_notes}
            onChange={set('inquiry_notes')}
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className="min-h-touch rounded-card px-4 py-3 text-ink-soft"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="min-h-touch rounded-card bg-accent px-5 py-3 text-white hover:bg-accent-hover disabled:opacity-60"
          >
            {submitting ? 'Saving…' : 'Save inquiry'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default QuickIntakeModal;
